// Phase 23B — Outlet detail page (storefront section + GPS drift banner from 23B.5.1)
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Camera, ArrowLeft, AlertTriangle, ChevronDown } from "lucide-react";
import { api, API_BASE, getToken, formatApiError, photoRefPath } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// GET /api/photos/{ref} requires a bearer token, which a plain <img src>
// can't send — fetch the bytes ourselves and hand the <img> an object URL.
async function fetchPhotoObjectUrl(ref) {
  if (!ref) return null;
  if (ref.startsWith("http") || ref.startsWith("blob:")) return ref;
  const res = await fetch(`${API_BASE}/photos/${photoRefPath(ref)}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`photo fetch failed: ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

export default function OutletDetail() {
  const { retailerId } = useParams();
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drift, setDrift] = useState(null);
  const [driftExpanded, setDriftExpanded] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);

  useEffect(() => {
    api.get(`/retailers/${retailerId}`)
      .then(r => setO(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    // 23B.5.1 — GPS drift (admin/manager only; 403 for others is expected)
    api.get(`/retailers/${retailerId}/gps-drift`)
      .then(r => setDrift(r.data))
      .catch(() => { /* SE viewer → 403 is expected; hide banner */ });
  }, [retailerId]);

  useEffect(() => {
    const ref = o?.storefront_photo_ref;
    if (!ref) { setPhotoSrc(null); return; }
    let objectUrl = null;
    let cancelled = false;
    fetchPhotoObjectUrl(ref).then((url) => {
      if (cancelled) return;
      objectUrl = url;
      setPhotoSrc(url);
    }).catch(() => { if (!cancelled) setPhotoSrc(null); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [o?.storefront_photo_ref]);

  if (loading) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>;
  if (!o) return <div className="p-6 text-neutral-500">Outlet not found.</div>;

  const photo = photoSrc;
  const meta = o.storefront_photo_meta || {};

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="outlet-detail-page">
      <Link to="/admin/outlet-transfer" className="inline-flex items-center gap-1 text-xs text-[#6D2C91] hover:underline">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Link>
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">{o.name}</h1>
        <p className="text-xs text-neutral-500 mt-0.5">
          <span className="font-mono">{o.code}</span> · {o.owner_name} · {o.phone}
        </p>
      </div>

      {/* Phase 23B.5.1 — GPS drift banner (admin/manager only) */}
      {drift?.drifted && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-3"
             data-testid="outlet-drift-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-amber-900">
                GPS drift detected —{" "}
                <span data-testid="outlet-drift-count">{drift.drifting_visits.length}</span>
                {" "}of{" "}
                <span data-testid="outlet-drift-total">{drift.total_visits}</span>
                {" "}visits &gt; {drift.threshold_m}m from creation location.
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Max drift: <b data-testid="outlet-drift-max">{drift.max_distance_m}m</b>.
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setDriftExpanded(v => !v)}
                     data-testid="outlet-drift-toggle">
              <ChevronDown className={`h-4 w-4 transition-transform ${driftExpanded ? "rotate-180" : ""}`} />
            </Button>
          </div>
          {driftExpanded && (
            <div className="mt-2 border-t border-amber-200 pt-2 space-y-1"
                 data-testid="outlet-drift-details">
              {drift.drifting_visits.map(v => (
                <div key={v.visit_id} className="flex items-center gap-2 text-xs"
                     data-testid={`outlet-drift-visit-${v.visit_id}`}>
                  <span className="font-mono text-neutral-500 w-32 shrink-0">
                    {v.ts ? new Date(v.ts).toLocaleString([], { dateStyle:"short", timeStyle:"short" }) : "—"}
                  </span>
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-mono">
                    {v.distance_m}m
                  </Badge>
                  <span className="text-neutral-600 truncate">
                    by <span className="font-mono">{(v.user_id || "").slice(0, 8)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-4 space-y-2 text-sm">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Basics</div>
            <Row k="Channel" v={o.channel_type} />
            <Row k="Status" v={<Badge className="bg-emerald-100 text-emerald-800">{o.status || "active"}</Badge>} />
            <Row k="Address" v={o.address} />
            <Row k="GPS" v={o.gps_lat && o.gps_lng ? `${o.gps_lat.toFixed(4)}, ${o.gps_lng.toFixed(4)}` : <span className="text-neutral-400">not geo-tagged</span>} />
            <Row k="Beat" v={o.beat_name ? `${o.beat_name}${o.beat_code ? ` (${o.beat_code})` : ""}` : (o.beat_id ? "—" : "unassigned")} />
            <Row k="Distributor" v={o.distributor_name ? `${o.distributor_name}${o.distributor_code ? ` (${o.distributor_code})` : ""}` : "—"} />
          </CardContent>
        </Card>

        <Card className="border-neutral-200 rounded-2xl" data-testid="outlet-storefront-section">
          <CardContent className="p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-1">
              <Camera className="h-3.5 w-3.5" /> Storefront
            </div>
            {photo ? (
              <>
                <div className="rounded-lg overflow-hidden bg-neutral-100 border border-neutral-200 aspect-[4/3] flex items-center justify-center relative"
                     data-testid="outlet-storefront-photo">
                  <img src={photo} alt="storefront" className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {meta.gps && (
                    <Badge className="bg-[#6D2C91]/10 text-[#6D2C91] border-[#6D2C91]/30 font-mono text-[10px]"
                            data-testid="outlet-storefront-gps">
                      <MapPin className="h-3 w-3 mr-1" />
                      {meta.gps.lat?.toFixed(4)}, {meta.gps.lng?.toFixed(4)}
                    </Badge>
                  )}
                  {meta.captured_at_ts && (
                    <Badge className="bg-neutral-100 text-neutral-700 text-[10px]"
                            data-testid="outlet-storefront-ts"
                            title={meta.captured_at_ts}>
                      captured {new Date(meta.captured_at_ts).toLocaleString()}
                    </Badge>
                  )}
                  {meta.uploader_user_id && (
                    <Badge className="bg-neutral-100 text-neutral-700 text-[10px] font-mono">
                      by {meta.uploader_user_id.slice(0, 8)}
                    </Badge>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-neutral-200 py-10 text-center text-neutral-400 text-sm"
                   data-testid="outlet-storefront-empty">
                No storefront photo captured
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="text-neutral-500 w-24 shrink-0">{k}</span>
      <span className="text-neutral-900 flex-1 min-w-0 break-words">{v ?? "—"}</span>
    </div>
  );
}
